import { useCallback, useEffect, useState } from "react";

import { toast } from "react-toastify";
import { useDispatch } from "react-redux";

import { verifyPaymentApi } from "../../../services/api/pitbPayzenApi";
import { updateUserPlan } from "../../../actions/userActions";

import { getActivatedPlanId, isPaymentSuccessful } from "../../paymentUtils";

export default function usePendingPsid({ selectedPlan }) {
  const dispatch = useDispatch();

  const [pendingPsid, setPendingPsid] = useState(() =>
    localStorage.getItem("pending_psid"),
  );

  const [psidData, setPsidData] = useState(() => {
    const savedData = localStorage.getItem("pending_psid_data");

    try {
      return savedData ? JSON.parse(savedData) : null;
    } catch {
      return null;
    }
  });

  const savePsid = useCallback((data, plan) => {
    const consumerNo = data?.consumerNumber || data?.psid || "";

    const persistedData = {
      ...data,

      planId: data?.planId || plan?.id,

      planTitle: data?.planTitle || plan?.title,
    };

    setPendingPsid(consumerNo);
    setPsidData(persistedData);

    localStorage.setItem("pending_psid", consumerNo);

    localStorage.setItem("pending_psid_data", JSON.stringify(persistedData));
  }, []);

  const clearPendingPsid = useCallback(() => {
    localStorage.removeItem("pending_psid");

    localStorage.removeItem("pending_psid_data");

    setPendingPsid(null);
    setPsidData(null);
  }, []);

  useEffect(() => {
    if (!pendingPsid) {
      return;
    }

    let isMounted = true;

    const checkStatus = async () => {
      try {
        const response = await verifyPaymentApi(pendingPsid);

        const isPaid = isPaymentSuccessful(response);

        if (!isPaid || !isMounted) {
          return;
        }

        const activatedId = getActivatedPlanId({
          response,
          psidData,
          selectedPlan,
        });

        toast.success(
          "Payment verified successfully! Your subscription is now active.",
        );

        if (activatedId) {
          dispatch(updateUserPlan(activatedId));
        }

        clearPendingPsid();
      } catch (error) {
        console.error("Background payment verification error:", error);
      }
    };

    checkStatus();

    const intervalId = setInterval(checkStatus, 6000);

    const handleWindowFocus = () => {
      checkStatus();
    };

    window.addEventListener("focus", handleWindowFocus);

    return () => {
      isMounted = false;

      clearInterval(intervalId);

      window.removeEventListener("focus", handleWindowFocus);
    };
  }, [pendingPsid, psidData, selectedPlan, dispatch, clearPendingPsid]);

  return {
    pendingPsid,
    psidData,
    savePsid,
    clearPsid: clearPendingPsid,
  };
}
